import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { colorPalette } from "../styles/theme";
import { DISTRESS_LEVELS, REGION_AVERAGE, DistressLevelLegend } from "./DailyDualLineChart";

/**
 * 기관 전체 마음 건강 추이 — 영역형 시계열 꺾은선 (기관 통계 리포트 섹션 1, 샘플)
 * - 실선 + 영역: 기관 전체 참가자의 평균 디스트레스 / 검은 점선: 지역 평균 기준선
 * - 개인 상세 그래프와 동일하게 Y축 소수점을 숨기고 4단계 배경 색상 구간 + 낮음~매우 높음 라벨로만 표시
 * - 일별 / 주별(월요일 시작 주 평균) 보기 전환
 */

const LINE_COLOR = "#8b5cf6";
const REGION_COLOR = "#111827";
const HEIGHT = 260;
const PADDING = { top: 16, right: 20, bottom: 32, left: 64 };
// 지역 평균과의 차이가 이 값 미만이면 "유사" (주간 요약 카드와 같은 기준)
const SIMILAR_THRESHOLD = 0.05;

const findLevel = (value) => DISTRESS_LEVELS.find((level) => value <= level.max) ?? DISTRESS_LEVELS[3];

const compareToRegion = (value) => {
  const diff = value - REGION_AVERAGE.distressAvg;
  if (Math.abs(diff) < SIMILAR_THRESHOLD) return "지역 평균과 유사";
  return `지역 평균보다 ${diff > 0 ? "높음" : "낮음"}`;
};

/** 부모 폭을 따라가는 차트용 — 요소의 현재 가로 폭을 추적 */
const useElementWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

/** 일별 데이터를 월요일 시작 주 단위 평균으로 묶음 — 라벨은 주 시작일(조회 시작일보다 앞서면 조회 시작일) */
const groupByWeek = (daily) => {
  const weeks = [];
  daily.forEach((d) => {
    const [y, m, day] = d.date.split("-").map(Number);
    const weekday = (new Date(y, m - 1, day).getDay() + 6) % 7; // 월=0 … 일=6
    const last = weeks[weeks.length - 1];
    if (!last || weekday === 0) weeks.push({ date: d.date, values: [d.value], days: 1 });
    else {
      last.values.push(d.value);
      last.days += 1;
    }
  });
  return weeks.map((w) => ({
    date: w.date,
    label: `${w.date.slice(5)} 주`,
    value: w.values.reduce((a, b) => a + b, 0) / w.values.length,
    days: w.days,
  }));
};

const LegendSwatch = ({ children, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Box component="svg" width={22} height={12} sx={{ display: "block", flexShrink: 0 }}>
      {children}
    </Box>
    <Typography sx={{ fontSize: 11, color: colorPalette.textSecondary, whiteSpace: "nowrap" }}>{label}</Typography>
  </Box>
);

/**
 * @param {Array} series - [{ date, org_distress_avg }] (조회 기간 일별)
 */
const OrgDistressAreaChart = ({ series }) => {
  const [unit, setUnit] = useState("day");
  const [containerRef, width] = useElementWidth();
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const daily = useMemo(
    // 실데이터는 아무도 기록하지 않은 날(null)이 있어 기록이 있는 날짜만 이어서 그림
    () =>
      series
        .filter((d) => typeof d.org_distress_avg === "number")
        .map((d) => ({ date: d.date, label: d.date.slice(5), value: d.org_distress_avg })),
    [series],
  );
  const data = unit === "week" ? groupByWeek(daily) : daily;

  // 기간 전체 평균 — 상단 한 줄 요약용 (수치 대신 단계·비교 문구)
  const periodAvg = daily.length ? daily.reduce((sum, d) => sum + d.value, 0) / daily.length : null;
  const periodLevel = periodAvg !== null ? findLevel(periodAvg) : null;

  const innerWidth = Math.max(0, width - PADDING.left - PADDING.right);
  const innerHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const xStep = data.length > 1 ? innerWidth / (data.length - 1) : 0;
  const xScale = (i) => (data.length > 1 ? PADDING.left + xStep * i : PADDING.left + innerWidth / 2);
  const yScale = (v) => PADDING.top + innerHeight - Math.min(Math.max(v, 0), 1) * innerHeight;

  const linePath = data.map((d, i) => `${i === 0 ? "M" : "L"} ${xScale(i)} ${yScale(d.value)}`).join(" ");
  const areaPath = data.length
    ? `${linePath} L ${xScale(data.length - 1)} ${yScale(0)} L ${xScale(0)} ${yScale(0)} Z`
    : "";
  // 날짜 라벨은 약 56px당 1개만 표시
  const labelEvery = Math.max(1, Math.ceil(56 / Math.max(xStep, 1)));
  const hovered = hoveredIndex !== null ? data[hoveredIndex] : null;

  return (
    <Box>
      {/* 상단: 기간 요약 + 일별/주별 전환 */}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
        {periodLevel && (
          <Typography sx={{ fontSize: 13, color: colorPalette.textPrimary }}>
            기간 평균{" "}
            <Box component="span" sx={{ fontWeight: 800, color: periodLevel.color === "#facc15" ? "#a16207" : periodLevel.color }}>
              {periodLevel.legend.split(" ")[0]} {periodLevel.stage} 단계
            </Box>{" "}
            <Box component="span" sx={{ color: colorPalette.textSecondary }}>
              · {compareToRegion(periodAvg)}
            </Box>
          </Typography>
        )}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={unit}
          onChange={(_, value) => {
            if (!value) return;
            setUnit(value);
            setHoveredIndex(null);
          }}
          sx={{
            ml: "auto",
            "& .MuiToggleButton-root": {
              px: 1.5,
              py: 0.4,
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
              color: colorPalette.textSecondary,
              borderColor: colorPalette.border,
            },
            "& .Mui-selected": {
              color: `${colorPalette.primary} !important`,
              backgroundColor: `${colorPalette.activeBg} !important`,
            },
          }}
        >
          <ToggleButton value="day">일별</ToggleButton>
          <ToggleButton value="week">주별</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* 선 종류 범례 */}
      <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5, mb: 1 }}>
        <LegendSwatch label="기관 전체 평균 디스트레스">
          <rect x="1" y="5" width="20" height="6" fill={LINE_COLOR} fillOpacity="0.2" />
          <line x1="1" x2="21" y1="5" y2="5" stroke={LINE_COLOR} strokeWidth="2.5" />
        </LegendSwatch>
        <LegendSwatch label="지역 평균 기준선">
          <line x1="1" x2="21" y1="6" y2="6" stroke={REGION_COLOR} strokeWidth="1.5" strokeDasharray="6 4" />
        </LegendSwatch>
      </Box>

      <Box ref={containerRef} sx={{ position: "relative", width: "100%", height: HEIGHT }}>
        {width > 0 && data.length > 0 && (
          <svg width={width} height={HEIGHT} role="img" aria-label="기관 전체 평균 디스트레스 추이" style={{ display: "block" }}>
            <defs>
              <linearGradient id="orgDistressArea" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={LINE_COLOR} stopOpacity="0.32" />
                <stop offset="100%" stopColor={LINE_COLOR} stopOpacity="0.04" />
              </linearGradient>
            </defs>

            {/* 4단계 배경 색상 구간 + 경계선 + Y축 구간 라벨 (소수점 비노출) */}
            {DISTRESS_LEVELS.map((level) => (
              <g key={level.axis}>
                <rect
                  x={PADDING.left}
                  y={yScale(level.max)}
                  width={innerWidth}
                  height={yScale(level.min) - yScale(level.max)}
                  fill={level.color}
                  fillOpacity="0.1"
                />
                <line
                  x1={PADDING.left}
                  x2={width - PADDING.right}
                  y1={yScale(level.max)}
                  y2={yScale(level.max)}
                  stroke={colorPalette.border}
                  strokeDasharray="4 4"
                />
                <text
                  x={PADDING.left - 8}
                  y={yScale((level.min + level.max) / 2) + 3}
                  textAnchor="end"
                  fontSize="10"
                  fill={colorPalette.textSecondary}
                >
                  {level.axis}
                </text>
              </g>
            ))}
            <line x1={PADDING.left} x2={width - PADDING.right} y1={yScale(0)} y2={yScale(0)} stroke={colorPalette.border} />

            {/* 기관 평균 영역 + 실선 */}
            <path d={areaPath} fill="url(#orgDistressArea)" />
            <path d={linePath} fill="none" stroke={LINE_COLOR} strokeWidth="2.5" strokeLinejoin="round" />

            {/* 지역 평균 기준선 (숫자 비노출) */}
            <line
              x1={PADDING.left}
              x2={width - PADDING.right}
              y1={yScale(REGION_AVERAGE.distressAvg)}
              y2={yScale(REGION_AVERAGE.distressAvg)}
              stroke={REGION_COLOR}
              strokeWidth="1.5"
              strokeDasharray="6 4"
            />
            <text
              x={PADDING.left + 6}
              y={yScale(REGION_AVERAGE.distressAvg) - 5}
              fontSize="10"
              fontWeight="700"
              fill={REGION_COLOR}
            >
              지역 평균 기준선
            </text>

            {/* 호버 가이드 + 점 */}
            {hovered && (
              <line
                x1={xScale(hoveredIndex)}
                x2={xScale(hoveredIndex)}
                y1={PADDING.top}
                y2={PADDING.top + innerHeight}
                stroke={colorPalette.textSecondary}
                strokeOpacity="0.35"
              />
            )}
            {data.map((d, i) => (
              <circle
                key={d.date}
                cx={xScale(i)}
                cy={yScale(d.value)}
                r={hoveredIndex === i ? 5 : unit === "week" || data.length <= 31 ? 3.5 : 0}
                fill={findLevel(d.value).color}
                stroke="#fff"
                strokeWidth="1.5"
                style={{ pointerEvents: "none" }}
              />
            ))}

            {/* x축 라벨 */}
            {data.map((d, i) =>
              i % labelEvery === 0 ? (
                <text key={d.date} x={xScale(i)} y={HEIGHT - 8} textAnchor="middle" fontSize="10" fill={colorPalette.textSecondary}>
                  {d.label}
                </text>
              ) : null,
            )}

            {/* 날짜(주)별 호버 영역 */}
            {data.map((d, i) => (
              <rect
                key={d.date}
                x={xScale(i) - Math.max(xStep, 16) / 2}
                y={PADDING.top}
                width={Math.max(xStep, 16)}
                height={innerHeight}
                fill="transparent"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex((prev) => (prev === i ? null : prev))}
              />
            ))}
          </svg>
        )}

        {/* 툴팁 — 수치 대신 단계와 지역 평균 비교만 표시 */}
        {hovered && (
          <Box
            sx={{
              position: "absolute",
              left: xScale(hoveredIndex),
              top: PADDING.top,
              transform: `translateX(${xScale(hoveredIndex) > width / 2 ? "calc(-100% - 12px)" : "12px"})`,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              backgroundColor: "#1a1f36",
              color: "#fff",
              fontSize: 12,
              lineHeight: 1.7,
              whiteSpace: "nowrap",
              pointerEvents: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
              zIndex: 1,
            }}
          >
            <Box sx={{ fontWeight: 700 }}>
              [{unit === "week" ? `${hovered.date} 시작 주 (${hovered.days}일 평균)` : hovered.date}]
            </Box>
            <Box>• 기관 평균: {findLevel(hovered.value).stage} 단계</Box>
            <Box>• {compareToRegion(hovered.value)}</Box>
          </Box>
        )}
      </Box>

      <DistressLevelLegend />
    </Box>
  );
};

export default OrgDistressAreaChart;
